import useHttpMutation from '@actions/services/firestore/browser/useHttpMutation';
import type { SystemToolExport } from '@actions/services/tools';
import type {
    UseImportToolParams,
    ImportToolParams,
    UseImportToolResult,
    ImportedTool,
} from './types';

interface ImportToolRequest {
    agentId: string;
    toolExport: SystemToolExport;
    secretMapping: Record<string, string>;
}

interface ImportToolResponse {
    tool: ImportedTool;
    report: {
        mappedSecrets: string[];
        unmappedSecrets: string[];
    };
}

/**
 * Hook for importing a tool from a JSON file
 *
 * Creates a new tool instance with secret mapping
 *
 * @example
 * ```tsx
 * const { importTool, isImporting } = useImportTool({
 *   onSuccess: () => toast.success('Tool imported!'),
 *   onError: (error) => toast.error(error.message)
 * });
 *
 * <button onClick={() => importTool({ agentId, toolExport, secretMapping })} disabled={isImporting}>
 *   {isImporting ? 'Importing...' : 'Import'}
 * </button>
 * ```
 */
const useImportTool = ({ onSuccess, onError }: UseImportToolParams = {}): UseImportToolResult => {
    const { mutate: importToolMutation, isLoading } = useHttpMutation<
        ImportToolRequest,
        ImportToolResponse
    >({
        method: 'POST',
        pathname: '/tools/import',
        onSuccess: (response: ImportToolResponse) => {
            console.log('Tool import successful:', response);
            onSuccess?.(response.tool);
        },
        onError:
            onError ||
            ((error: Error) => {
                console.error('Error importing tool:', error);
            }),
    });

    const importTool = async ({ agentId, toolExport, secretMapping }: ImportToolParams) => {
        return importToolMutation({
            body: {
                agentId,
                toolExport,
                secretMapping,
            },
        });
    };

    return {
        importTool,
        isImporting: isLoading,
    };
};

export default useImportTool;
